/**
 * Authentication Service
 * Handles user authentication using Supabase Auth
 */

import { supabase } from '../config/supabase';

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{user, session, error}>}
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      return { user: null, session: null, error };
    }

    return { user: data.user, session: data.session, error: null };
  } catch (error) {
    console.error('Sign in exception:', error);
    return { user: null, session: null, error };
  }
};

/**
 * Sign out current user
 * @returns {Promise<{error}>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Sign out error:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Sign out exception:', error);
    return { error };
  }
};

/**
 * Get current session
 * @returns {Promise<{session, error}>}
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Get session error:', error);
      return { session: null, error };
    }

    return { session: data.session, error: null };
  } catch (error) {
    console.error('Get session exception:', error);
    return { session: null, error };
  }
};

/**
 * Get current user
 * @returns {Promise<{user, error}>}
 */
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error('Get user error:', error);
      return { user: null, error };
    }

    return { user: data.user, error: null };
  } catch (error) {
    console.error('Get user exception:', error);
    return { user: null, error };
  }
};

/**
 * Subscribe to auth state changes
 * @param {function} callback - Callback function to handle auth state changes
 * @returns {object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });

  return subscription;
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  const { session } = await getSession();
  return session !== null;
};

/**
 * Reset password for email
 * @param {string} email - User email
 * @param {string} redirectTo - URL to redirect to after password reset
 * @returns {Promise<{error}>}
 */
export const resetPasswordForEmail = async (email, redirectTo) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo || `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      console.error('Reset password error:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Reset password exception:', error);
    return { error };
  }
};

/**
 * Update user password
 * @param {string} newPassword - New password
 * @returns {Promise<{error}>}
 */
export const updatePassword = async (newPassword) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      console.error('Update password error:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Update password exception:', error);
    return { error };
  }
};

/**
 * Get user role from app_metadata
 * @returns {Promise<string>} User role (defaults to 'Integración')
 */
export const getUserRole = async () => {
  try {
    const { user, error } = await getCurrentUser();
    
    if (error || !user) {
      return 'Integración';
    }

    return user.app_metadata?.role || 'Integración';
  } catch (error) {
    console.error('Get user role exception:', error);
    return 'Integración';
  }
};

export const authService = {
  signIn,
  signOut,
  getSession,
  getCurrentUser,
  onAuthStateChange,
  isAuthenticated,
  resetPasswordForEmail,
  updatePassword,
  getUserRole
};
